import { useState, useEffect } from 'react';
import { X, ExternalLink, Copy, Check } from 'lucide-react';
import { useApp } from '../context/AppContext';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    quoteId: number;
    quoteDate: string; // ISO string "YYYY-MM-DD…"
}

interface EmailService {
    id: string;
    name: string;
    url: string;
    icon: string;
    hint: string;
}

function buildServices(searchQuery: string): EmailService[] {
    const q = encodeURIComponent(searchQuery);
    return [
        {
            id: 'gmail',
            name: 'Gmail',
            url: `https://mail.google.com/mail/u/0/#search/${q}`,
            icon: '📧',
            hint: 'mail.google.com',
        },
        {
            id: 'outlook',
            name: 'Outlook',
            url: `https://outlook.live.com/mail/0/?search=${q}`,
            icon: '📨',
            hint: 'outlook.live.com',
        },
        {
            id: 'yahoo',
            name: 'Yahoo Mail',
            url: `https://mail.yahoo.com/d/search/keyword=${q}`,
            icon: '🔶',
            hint: 'mail.yahoo.com',
        },
        {
            id: 'proton',
            name: 'ProtonMail',
            url: `https://mail.proton.me/u/0/#type=folder&id=5&search=${q}`,
            icon: '🔒',
            hint: 'mail.proton.me',
        },
        {
            id: 'roundcube',
            name: 'Roundcube / Webmail',
            url: `https://www.google.com/search?q=roundcube+webmail+${q}`,
            icon: '🌐',
            hint: 'Recherche Google →',
        },
    ];
}

export default function EmailServiceSelector({ isOpen, onClose, quoteId, quoteDate }: Props) {
    const { lang } = useApp();
    const [copied, setCopied] = useState(false);

    const searchQuery = `lodiss devis ${quoteId}`;
    const formattedDate = quoteDate ? quoteDate.slice(0, 10) : '';
    const emailSubjectPreview = `Lodiss - Devis #${quoteId} - ${formattedDate}`;

    const services = buildServices(searchQuery);

    useEffect(() => {
        if (!isOpen) setCopied(false);
    }, [isOpen]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(searchQuery);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* silent */
        }
    };

    if (!isOpen) return null;

    return (
        /* Overlay */
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-150 px-4"
            onClick={onClose}
        >
            {/* Modal */}
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* ── Header ──────────────────────────────────────────── */}
                <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4">
                    <div>
                        <h2 className="text-base font-bold text-gray-900">
                            {lang === 'fr' ? 'Ouvrir dans votre messagerie' : 'Open in your email client'}
                        </h2>
                        <p className="text-xs text-gray-400 mt-0.5">
                            {lang === 'fr'
                                ? 'Recherche pré-remplie pour retrouver votre devis'
                                : 'Pre-filled search to find your quote'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* ── Search preview ───────────────────────────────────── */}
                <div className="mx-6 mb-5 bg-gray-50 rounded-xl p-4 space-y-1.5 border border-gray-100">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            {lang === 'fr' ? 'Filtre de recherche' : 'Search filter'}
                        </span>
                        <code className="ml-auto text-xs font-mono bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-800 select-all">
                            {searchQuery}
                        </code>
                    </div>
                    <p className="text-xs text-gray-400">
                        {lang === 'fr' ? 'Email attendu :' : 'Expected email subject:'}{' '}
                        <span className="text-gray-600 font-medium">{emailSubjectPreview}</span>
                    </p>
                </div>

                {/* ── Services list ────────────────────────────────────── */}
                <div className="px-6 pb-4 space-y-2">
                    {services.map(svc => (
                        <div
                            key={svc.id}
                            className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors"
                        >
                            <span className="text-xl leading-none">{svc.icon}</span>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-800 leading-tight">{svc.name}</p>
                                <p className="text-xs text-gray-400 truncate">{svc.hint}</p>
                            </div>
                            <a
                                href={svc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-lodiss-red border border-lodiss-red/30 rounded-lg hover:bg-lodiss-red hover:text-white transition-colors flex-shrink-0"
                            >
                                {lang === 'fr' ? 'Ouvrir' : 'Open'}
                                <ExternalLink size={11} />
                            </a>
                        </div>
                    ))}

                    {/* ── Copy fallback ──────────────────────────────────── */}
                    <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors">
                        <span className="text-xl leading-none">📋</span>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 leading-tight">
                                {lang === 'fr' ? 'Autre / Copier' : 'Other / Copy'}
                            </p>
                            <p className="text-xs text-gray-400">
                                {lang === 'fr' ? 'Coller dans n\'importe quelle messagerie' : 'Paste in any email client'}
                            </p>
                        </div>
                        <button
                            onClick={handleCopy}
                            className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors flex-shrink-0 ${copied
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'text-gray-500 border-gray-200 hover:border-lodiss-red hover:text-lodiss-red'
                                }`}
                        >
                            {copied ? (
                                <>
                                    <Check size={11} />
                                    {lang === 'fr' ? 'Copié !' : 'Copied!'}
                                </>
                            ) : (
                                <>
                                    <Copy size={11} />
                                    {lang === 'fr' ? 'Copier' : 'Copy'}
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* ── Footer ──────────────────────────────────────────── */}
                <div className="px-6 pb-6">
                    <button
                        onClick={onClose}
                        className="w-full py-2.5 text-sm font-semibold text-gray-400 hover:text-gray-600 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors"
                    >
                        {lang === 'fr' ? 'Annuler' : 'Cancel'}
                    </button>
                </div>
            </div>
        </div>
    );
}
